
import type { ClusterFeature } from '@/modules/YandexMap3Module/map-cluster.types'
import type {
    ClusterFeatureEntity,
    DispatcherWorkplaceAssignPayload,
    DispatcherWorkplaceTake,
    DispatcherWorkplaceAssignCourier,
    DispatcherWorkplaceLoadCouriersParams,
} from '@/modules/DispatcherWorkplaceModule/types'

import type { PropType } from 'vue'

import { defineComponent, onBeforeMount, reactive } from 'vue'

import DwPopup from '@/modules/DispatcherWorkplaceModule/ui/DwPopup/index.vue'
import DwButton from '@/modules/DispatcherWorkplaceModule/ui/DwButton/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

import useNotifications from '@/common/composable/useNotifications'

import { dispatcherWorkplaceAPI } from '@/modules/DispatcherWorkplaceModule/services'

import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

export default defineComponent({
    components: {
        DwPopup,
        DwButton,
        TheSelect,
    },
    props: {
        feature: {
            type: [Object, null] as PropType<ClusterFeature<ClusterFeatureEntity> | null>,
            default: null,
        },
        dispatcherSectorId: {
            type: [Number, null] as PropType<number | null>,
            default: null,
        },
    },
    emits: ['close', 'assigned'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            courierId: null as number | null,
        })

        const validation$ = useVuelidate({ courierId: { required } }, state)

        const couriers_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as DispatcherWorkplaceAssignCourier[],
        })

        const loadCouriers = async () => {
            try {
                couriers_state.is_loading = true

                const params: DispatcherWorkplaceLoadCouriersParams = {
                    page: 1,
                    limit: 9999,
                    statusIds: [2],
                    typesIds: [1],
                }

                if (props.dispatcherSectorId) {
                    params.dispatcherSectorIds = [props.dispatcherSectorId]
                }

                const response = await dispatcherWorkplaceAPI.loadCouriers(params)
                couriers_state.list = response.data.data

                couriers_state.is_failed = false
            } catch (error) {
                console.error(error)
                couriers_state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении списка курьеров произошла ошибка',
                })
            } finally {
                couriers_state.is_loading = false
            }
        }

        const onClose = () => {
            if (state.is_loading) return
            emit('close')
        }

        const onSubmit = async () => {
            if (
                !props.feature ||
                !props.feature.entity ||
                props.feature.entity.type !== 1 ||
                state.is_loading
            )
                return

            validation$.value.$touch()
            if (validation$.value.$invalid) return

            try {
                state.is_loading = true

                const payload: DispatcherWorkplaceAssignPayload = {
                    courierId: state.courierId as number,
                    orderIds: [(props.feature.entity as DispatcherWorkplaceTake).orderId],
                }

                await dispatcherWorkplaceAPI.assignOrder(payload)

                notifications.notify({ type: 'success', title: 'Заказ переназначен' })
                emit('assigned', props.feature)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При переназначении заказа произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(loadCouriers)

        return {
            state,
            validation$,
            couriers_state,
            loadCouriers,
            onClose,
            onSubmit,
        }
    },
})
